'use client';

import { useMemo } from 'react';
import { SexType } from '@/apiClient';
import {
  Box,
  Card,
  CardContent,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { TRowValue } from '@/hooks/studbook/animal/useAnimalFilter';
import { black, slate } from '@/lib/colours';
import { format } from 'date-fns';
import { Divider } from '@/primitives';
import Link from 'next/link';
import MemberInlineDisplay from '../Member/MemberInlineDisplay';
import useStudbookContext from '@/studbook/store/useStudbookContext';
import useOrgNameFromOrgId from '@/hooks/studbook/useOrgNameFromOrgId';
import { parseJson } from '@/lib/common';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  borderRadius: '8px',
  overflow: 'hidden',
  background: theme.palette.grey[100],
  boxShadow: theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive, theme }) => ({
  height: '24px',
  width: 'fit-content',
  borderRadius: '4px',
  padding: '4px 12px',
  color: 'white',
  fontSize: '14px',
  lineHeight: '16px',
  textTransform: 'capitalize',
  backgroundColor: isActive
    ? theme.palette.success.main
    : theme.palette.text.disabled,
}));

const Name = styled(Link)(({ theme }) => ({
  display: '-webkit-box',
  lineClamp: 1,
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  wordBreak: 'break-word',
  textOverflow: 'ellipsis',
  color: theme.palette.info.main,
  fontSize: '18px',
  fontWeight: 500,
  textDecoration: 'none',
}));

const Image = styled('img')({
  borderRadius: '4px',
  height: '56px',
  width: '56px',
  objectFit: 'contain',
});

const Grid = styled(CardContent)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  columnGap: '16px',
  rowGap: '4px',
  padding: 0,
});

const Cell = styled(Box)({
  width: 'calc(50% - 8px)',
  display: 'flex',
  flexDirection: 'column',
  height: '44px',
});

const PropsHeader = styled(Typography)({
  color: slate[500],
  fontSize: '14px',
  lineHeight: '16px',
});

const PropsValue = styled(Typography)({
  display: '-webkit-box',
  lineClamp: 1,
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  wordBreak: 'break-word',
  textOverflow: 'ellipsis',
  alignItems: 'center',
  color: black[900],
  fontSize: '16px',
  lineHeight: '20px',
  height: '20px',
});

const Footer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: '4px',
  height: '44px',
});

const PropsLink = styled(Name)({
  lineClamp: 1,
  WebkitLineClamp: 1,
  fontSize: '16px',
  lineHeight: '20px',
  height: '20px',
});

type AnimalCardProps = {
  guid: TRowValue;
  name: TRowValue;
  sex: TRowValue;
  identifier: TRowValue;
  birthDate?: TRowValue;
  color?: TRowValue;
  sire?: TRowValue;
  dam?: TRowValue;
  breeder?: TRowValue;
  owner?: TRowValue;
  status: TRowValue;
  imgUrl: string;
  imgError: boolean;
  isLoadingImg: boolean;
};

export default function AnimalCard({
  imgUrl,
  imgError,
  name,
  sex,
  identifier,
  birthDate,
  color,
  sire,
  dam,
  breeder,
  owner,
  status,
  isLoadingImg,
}: AnimalCardProps) {
  const { orgId } = useStudbookContext();
  const orgName = useOrgNameFromOrgId(orgId || '');
  const parsedSexValue = useMemo(
    () => parseJson(sex.value as string, {}),
    [sex],
  );
  const parsedType = Number(parsedSexValue.sexType);

  return (
    <StyledCard>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Box height="24px" mb="8px">
            {status.value && (
              <StatusBadge isActive={status.value === 'Active'}>
                {String(status.value).toLowerCase()}
              </StatusBadge>
            )}
          </Box>
          <Box height="48px">
            {name?.id && orgName && (
              <Tooltip disableInteractive title="View Details">
                <Name href={`/${orgName}${PUBLIC_ROUTES.ANIMAL}/${name.id}`}>
                  {name.value}
                </Name>
              </Tooltip>
            )}
          </Box>
        </Box>
        {!imgError && !isLoadingImg && imgUrl && orgName && (
          <Link href={`/${orgName}${PUBLIC_ROUTES.ANIMAL}/${name.id}`}>
            <Image alt="animal image" src={imgUrl} />
          </Link>
        )}
      </Box>
      <Divider sx={{ my: '16px' }} />
      <Grid>
        <Cell>
          <PropsHeader>Sex</PropsHeader>
          <PropsValue
            sx={{
              display: 'flex !important',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {parsedType === SexType.Male && (
              <MaleIcon sx={{ color: 'blue', fontSize: '16px' }} />
            )}
            {parsedType === SexType.Female && (
              <FemaleIcon sx={{ color: 'hotpink', fontSize: '16px' }} />
            )}
            {parsedType === SexType.DesexedMale && (
              <Image
                src="/neuter-sign.png"
                alt="Steer icon"
                width={16}
                height={16}
              />
            )}
          </PropsValue>
        </Cell>
        <Cell>
          <PropsHeader>Identifier</PropsHeader>
          <PropsValue sx={{ wordBreak: 'break-all' }}>
            {identifier.value}
          </PropsValue>
        </Cell>
        <Cell>
          <PropsHeader>Date of Birth</PropsHeader>
          <PropsValue>
            {birthDate?.value && format(birthDate.value, 'dd/MM/yyyy')}
          </PropsValue>
        </Cell>
        <Cell>
          <PropsHeader>Colour</PropsHeader>
          <PropsValue>{color?.value}</PropsValue>
        </Cell>
      </Grid>
      <Footer>
        <PropsHeader>Owner</PropsHeader>
        <PropsValue>
          {owner?.id && orgName && (
            <Tooltip disableInteractive title="View Details">
              <MemberInlineDisplay
                memberDisplayName={String(owner.value)}
                memberId={Number(owner.id)}
                orgName={orgName!}
                linkStyle={{
                  textDecoration: 'none',
                  lineClamp: 1,
                  WebkitLineClamp: 1,
                  fontSize: '16px',
                  lineHeight: '20px',
                  height: '20px',
                  paddingInline: 0,
                  minWidth: 0,
                  backgroundColor: 'transparent !important',
                }}
              />
            </Tooltip>
          )}
        </PropsValue>
      </Footer>
      <Footer>
        <PropsHeader>Sire</PropsHeader>
        <PropsValue>
          {sire?.id && orgName && (
            <Tooltip disableInteractive title="View Details">
              <PropsLink href={`/${orgName}${PUBLIC_ROUTES.ANIMAL}/${sire.id}`}>
                {sire.value}
              </PropsLink>
            </Tooltip>
          )}
        </PropsValue>
      </Footer>
      <Footer>
        <PropsHeader>Dam</PropsHeader>
        <PropsValue>
          {dam?.id && orgName && (
            <Tooltip disableInteractive title="View Details">
              <PropsLink href={`/${orgName}${PUBLIC_ROUTES.ANIMAL}/${dam.id}`}>
                {dam.value}
              </PropsLink>
            </Tooltip>
          )}
        </PropsValue>
      </Footer>
    </StyledCard>
  );
}
